/*@flow*/
import type { Element } from 'react'; /** @jsx jsx */
import { css, jsx } from '@emotion/core';
import { useContext, useEffect, useState } from 'react';
import commands from './commands';
import cx from 'classnames';
import MetricsContext from './MetricsContext';
import OverlayFactory from 'comps/OverlayFactory';
import { Tooltip } from 'react-bootstrap';
import type { Analysis$Field } from './types';
import * as t from 'lib/t';
import NonEmptyString from 'lib/NonEmptyString';
import Row from './Row';
import FormField from './FormField';
import SelectDropdown from './SelectDropdown';
import truncate from 'underscore.string/truncate';

const RED = '#f44336';
const lineBreakRegExp = /(\r\n|\n|\r)/gm;

const TooltipWrapper = OverlayFactory(Tooltip);

const StringType = t.mysubtype(NonEmptyString, (v) => {
  return !v ? 'Invalid entry.' : null;
});

const FormulaType = t.mysubtype(StringType, (v) => {
  return v && !v.replace(lineBreakRegExp, '').match(/\{.*value.*\}.*/)
    ? 'Not a valid formula.'
    : null;
});

const schema = t.struct({
  id: t.maybe(t.union([t.String, t.Number])),
  name: StringType,
  description: t.maybe(t.String),
  source: t.maybe(t.String),
  table: StringType,
  op: StringType,
  column: t.maybe(StringType),
  formula: FormulaType,
});

export default (): Element<string> => {
  const ctx = useContext(MetricsContext);
  const tables = ctx.state.tables.map((v) => ({
    value: v.table,
    label: v.name,
  }));
  const [fields, setFields] = useState([
    { __typename: 'PlaceMetricAnalysisField', name: 'cartodb_id', label: 'id' },
  ]);
  const setField = (key) => (value) =>
    commands(ctx).set(`widgetForm.${key}`, value);
  const getField = (key) => ctx.state.widgetForm[key];
  const table = getField('table');

  useEffect(() => {
    if (!table) return;
    const tableConfig = ctx.state.tables.find((v) => v.table === table);
    if (!tableConfig) return;
    const f: Analysis$Field[] = tableConfig.fields;
    // Load the fields of selected tableConfig
    setFields(f || []);
  }, [table]);

  const op = getField('op');
  const column = getField('column');
  const enableColumnDropdown = table && op !== 'count';

  useEffect(() => {
    if (!op) {
      setField('op')('count');
    }
  }, []);

  const [validationErrors, setValidationErrors] = useState({});
  const [isValidated, setValidated] = useState(false);

  const getError = (name) => isValidated && validationErrors[name];

  const Required = ({ children }) => (
    <TooltipWrapper placement="left" content="Required" id="required-field">
      <span className="required">{children}</span>
    </TooltipWrapper>
  );

  const validate = () => {
    const value = ctx.state.widgetForm;
    const { errors } = t.validate(value, schema);
    setValidated(true);
    if (errors.length === 0) return true;
    // Set validation errors
    setValidationErrors(() => {
      const fn = (s, v) => ({ ...s, [v.path[0]]: v.message });
      return (errors.reduce(fn, {}): any);
    });
  };

  const ErrorMessage = (props) =>
    getError(props.name) ? (
      <span className="error">{getError(props.name)}</span>
    ) : null;

  return (
    <div
      css={css(`
        .required { &::after{ content: '*'; color: ${RED};} }
        .hasValidationError { border-bottom: solid 1px ${RED}; padding-top: 10px; }
        .error { color: ${RED}; font-size: 0.9rem; }
      `)}
    >
      <Row
        styles={`
          min-height: 3.4rem;
        `}
      >
        <span />
        <div css={css('font-weight: lighter; font-size: 1.2rem;')}>
          ANALYSIS
        </div>
      </Row>
      <Row>
        <span>
          <Required>Name</Required>
        </span>
        <div>
          <div
            className={cx({
              hasValidationError: getError('name'),
            })}
          >
            <FormField name="name" placeholder="Name" minWidth="180px" />
          </div>
          <ErrorMessage name="name" />
        </div>
      </Row>
      <Row>
        <span>Source</span>
        <div>
          <div
            className={cx({
              hasValidationError: getError('source'),
            })}
          >
            <FormField name="source" placeholder="Source" minWidth="180px" />
          </div>
          <ErrorMessage name="source" />
        </div>
      </Row>
      <Row>
        <span>Descr.</span>
        <div>
          <div
            className={cx({
              hasValidationError: getError('description'),
            })}
          >
            <FormField
              name="description"
              placeholder="Description"
              minWidth="180px"
            />
          </div>
          <ErrorMessage name="description" />
        </div>
      </Row>
      <Row>
        <span>
          <Required>Data</Required>
        </span>
        <div>
          <div
            className={cx({
              hasValidationError: getError('table'),
            })}
          >
            <SelectDropdown
              value={table}
              onChange={(e) => {
                e.target && setField('table')(e.target.value);
              }}
            >
              <option value=""> -- Select table --</option>
              {tables.map((v, i) => (
                <option value={v.value} key={i}>
                  {truncate(v.label, 20)}
                </option>
              ))}
            </SelectDropdown>
          </div>
          <ErrorMessage name="table" />
        </div>
      </Row>
      <Row>
        <span>
          <Required>Op</Required>
        </span>
        <div>
          <div
            className={cx({
              hasValidationError: getError('op'),
            })}
          >
            <SelectDropdown
              key={table}
              value={op}
              onChange={(e) => {
                e.target && setField('op')(e.target.value);
              }}
            >
              <option value="count">COUNT</option>
              <option value="sum">SUM</option>
              <option value="avg">AVG</option>
            </SelectDropdown>
          </div>
          <ErrorMessage name="op" />
        </div>
      </Row>
      <Row disabled={!enableColumnDropdown}>
        <span>Column</span>
        <div>
          <div
            className={cx({
              hasValidationError: getError('column'),
            })}
          >
            <SelectDropdown
              key={fields.join('')}
              disabled={!enableColumnDropdown}
              value={column}
              onChange={(e) => {
                enableColumnDropdown &&
                  e.target &&
                  setField('column')(e.target.value);
              }}
            >
              {fields.map((v, i) => (
                <option value={v.name} key={i}>
                  {v.label}
                </option>
              ))}
            </SelectDropdown>
          </div>
          <ErrorMessage name="column" />
        </div>
      </Row>
      <Row>
        <span>
          <Required>Formula</Required>
        </span>
        <div>
          <div
            css={css(`
              display: grid;
              grid-template-columns: 87% 13%;
            `)}
          >
            <div>
              <div
                className={cx({
                  hasValidationError: getError('formula'),
                })}
              >
                <FormField
                  name="formula"
                  placeholder="Formula"
                  minWidth="100px"
                />
              </div>
              <ErrorMessage name="formula" />
            </div>
            <div>
              <TooltipWrapper
                content={<span>You can use algebra inside the braces. </span>}
                placement="left"
                id="formula-tooltip"
                trigger={['hover']}
                innerProps={{
                  css: css('font-size: 1rem;'),
                }}
              >
                <i
                  className="fa fa-info-circle"
                  css={css('font-size: 1rem; color: #999; cursor: pointer;')}
                />
              </TooltipWrapper>
            </div>
          </div>
        </div>
      </Row>
      <Row>
        <span />
        <div
          css={css(`
            padding-top: 3px;
            button { display: inline-block; margin: 0; font-size: 1.2rem; }
        `)}
        >
          <button
            className="btn btn-info"
            onClick={() => {
              if (validate()) {
                commands(ctx).doneEditing();
              }
            }}
          >
            <b>RUN</b>
          </button>
          <button
            className="btn btn-link"
            onClick={() => {
              commands(ctx).startCustomizing();
            }}
          >
            Cancel
          </button>
        </div>
      </Row>
    </div>
  );
};
